import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import SiteHeader from "@/components/SiteHeader";
import BookingConfirmedWatcher from "@/components/BookingConfirmedWatcher";
import Home from "@/pages/Home";
import Landing from "@/pages/Landing";
import Waitlist from "@/pages/Waitlist";
import LiveRoom from "@/pages/LiveRoom";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Orders from "@/pages/Orders";
import Profile from "@/pages/Profile";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLives from "@/pages/admin/AdminLives";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPriceEngine from "@/pages/admin/AdminPriceEngine";
import AdminDropOverview from "@/pages/admin/AdminDropOverview";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import HostLiveRoom from "@/pages/admin/HostLiveRoom";

function AdminRoute({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return null;
  if (!admin) return <Navigate to="/admin" replace />;
  return children;
}

/** Access gate: allow admins + users with buyer-side access granted. Everyone else → Landing (public) or Waitlist (signed-in but no access yet). */
function AccessRoute({ children }) {
  const { user, admin, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (admin) return children;
  if (user?.access_granted) return children;
  if (user) return <Navigate to="/waitlist" replace />;
  const next = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/?next=${next}`} replace />;
}

/** LiveRoom / paid flows need a signed-in user AND buyer-side access */
function UserRoute({ children }) {
  const { user, admin, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (admin) return children;
  if (user?.access_granted) return children;
  if (user) return <Navigate to="/waitlist" replace />;
  const next = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/login?next=${next}`} replace />;
}

/** Live preview route: lets ANYONE view a scheduled drop (PreLive experience).
 *  - Anonymous → allowed (LiveRoom will render PreLive with a sign-in CTA).
 *  - Admin / access-granted user → allowed (full flow).
 *  - Signed-in but no access → redirected to /waitlist so they don't leak past the gate. */
function LiveViewerRoute({ children }) {
  const { user, admin, loading } = useAuth();
  if (loading) return null;
  if (admin) return children;
  if (user?.access_granted) return children;
  if (user) return <Navigate to="/waitlist" replace />;
  return children; // anonymous — pass through
}

/** Smart root: Landing for public, Home for admins/access-granted, Waitlist for others */
function RootRoute() {
  const { user, admin, loading } = useAuth();
  if (loading) return null;
  if (admin) return <Home />;
  if (user?.access_granted) return <Home />;
  if (user) return <Waitlist />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();

  // Fire an SPA virtual pageview into dataLayer on every route change so GTM
  // (and any GA4 config it wires) records client-side navigations. GTM's
  // built-in All Pages trigger only catches the initial document load.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "spa_page_view",
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  // CRITICAL: Detect OAuth callback synchronously during render (before AuthProvider /me check)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  const isLanding = location.pathname === "/" || location.pathname === "/waitlist";
  const isLive = location.pathname.startsWith("/live/");

  return (
    <>
      {!isLanding && !isLive && <SiteHeader />}
      <BookingConfirmedWatcher />
      <Routes>
        {/* PUBLIC — landing + waitlist */}
        <Route path="/" element={<RootRoute />} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/login" element={<Login />} />

        {/* FOUNDING-MEMBERS-ONLY app routes */}
        <Route path="/home" element={<AccessRoute><Home /></AccessRoute>} />
        <Route path="/orders" element={<AccessRoute><Orders /></AccessRoute>} />
        <Route path="/profile" element={<AccessRoute><Profile /></AccessRoute>} />
        <Route path="/live/:id" element={<LiveViewerRoute><LiveRoom /></LiveViewerRoute>} />

        {/* ADMIN */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/lives" element={<AdminRoute><AdminLives /></AdminRoute>} />
        <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
        <Route path="/admin/bookings" element={<AdminRoute><AdminBookings /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="/admin/waitlist" element={<AdminRoute><AdminWaitlist /></AdminRoute>} />
        <Route path="/admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
        <Route path="/admin/price-engine" element={<AdminRoute><AdminPriceEngine /></AdminRoute>} />
        <Route path="/admin/drops" element={<AdminRoute><AdminDropOverview /></AdminRoute>} />
        <Route path="/admin/live/:id" element={<AdminRoute><HostLiveRoom /></AdminRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" theme="dark" />
      </BrowserRouter>
    </AuthProvider>
  );
}
